"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bundleLineSchema = exports.BUNDLE_LINE_SCHEMAS = exports.bundleTrailerLineSchema = exports.bundleClinicianLineSchema = exports.bundleLabelLineSchema = exports.bundleActLineSchema = exports.bundleGrpLineSchema = exports.bundlePmLineSchema = exports.bundleRecordLineSchema = exports.bundlePersonLineSchema = exports.bundleFileLineSchema = exports.bundleOrgLineSchema = exports.bundleManifestLineSchema = exports.bundleIntakeApprovalSchema = exports.BUNDLE_GATES = exports.BUNDLE_CLINICIAN_NAME_SOURCES = exports.BUNDLE_ROW_KINDS = exports.BUNDLE_OUTCOMES = exports.BUNDLE_MEASURE_IDS = exports.BUNDLE_OPTIONAL_PROPERTIES = void 0;
// The Analysis results bundle, VBLTC_BUNDLE_V1 (CR-004, J-180; CR-004-SPEC.md section 5.3). One gzip of
// UTF-8 NDJSON: a manifest line, then org, file, person, record, pm, grp, act and label lines in that
// order, then a trailer. The executable definition is the contract corpus in
// `config/demo/bundles/contract/` (`bundle.schema.json`, Analysis `hmcanon/bundle_contract.py`): every
// property is always present ("optional" means nullable) and no other property is allowed, so every
// object here is strict. The exceptions are the properties `BUNDLE_OPTIONAL_PROPERTIES` names, added after
// v0 (J-274, J-275): they may be absent as well as null, so a bundle written before them still loads.
// `bundle.test.ts` checks these schemas against that file.
// J-273 (QB3 item 10): the clinician line, oversight's contract of 2026-10-17, which the corpus does not
// carry yet: one line per named rendering NPI, anywhere between the manifest and the trailer (Analysis
// writes them after the label lines), counted as `counts.clinician`, which a bundle without clinician
// lines may leave out. A bundle without clinician lines loads as before.
const zod_1 = require("zod");
const npi_js_1 = require("./npi.js");
const DATE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const DATE_TIME = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}Z$/;
const SHA256 = /^[0-9a-f]{64}$/;
const TIN = /^[0-9]{9}$/;
const NPI = /^[0-9]{10}$/;
const text = () => zod_1.z.string().min(1);
const nullableText = () => text().nullable();
const date = () => zod_1.z.string().regex(DATE);
const count = () => zod_1.z.int().min(0);
const gate = () => zod_1.z.enum(["PASS", "FAIL"]);
/**
 * The properties a line may leave out, by kind (J-274's `discharge_date`, J-275's `facility_name`): the
 * contract lists them without requiring them. Absent means the same as null.
 */
exports.BUNDLE_OPTIONAL_PROPERTIES = {
    person: ["discharge_date"],
    record: ["facility_name"],
    act: ["facility_name"],
};
/**
 * J-275 (QB3 item 13): the visit's facility (nursing home, SNF or ALF) as the source names it; optional,
 * absent or null meaning unknown (HealthDrive/Ethizo visits name none).
 */
const facilityName = () => nullableText().optional();
/** The five measures of the 2026 rule set, as Analysis numbers them. */
exports.BUNDLE_MEASURE_IDS = ["001", "112", "113", "134", "236"];
const measureId = () => zod_1.z.enum(exports.BUNDLE_MEASURE_IDS);
/** The four outcomes of a patient-measure row (A row). */
exports.BUNDLE_OUTCOMES = ["met", "not_met", "excluded", "exception"];
/** The line kinds between the manifest and the trailer, in the order a bundle holds them. */
exports.BUNDLE_ROW_KINDS = [
    "org",
    "file",
    "person",
    "record",
    "pm",
    "grp",
    "act",
    "label",
    "clinician",
];
/** The two sources Analysis names a clinician from (oversight's contract, 2026-10-17). */
exports.BUNDLE_CLINICIAN_NAME_SOURCES = ["NPPES", "HEALTHMONIX"];
exports.BUNDLE_GATES = ["G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8", "G9"];
const outcomeCountsSchema = zod_1.z.strictObject({
    met: count(),
    not_met: count(),
    excluded: count(),
    exception: count(),
});
const manifestHashSchema = zod_1.z.strictObject({
    rows: zod_1.z.int().min(1),
    outcome_counts: outcomeCountsSchema,
    a_sha256: zod_1.z.string().regex(SHA256),
    completeness: zod_1.z.strictObject({
        registry_reported: count(),
        completeness_pct: zod_1.z.number().min(0).max(100),
    }),
});
/**
 * S-08 (J-394): the owner's intake approval this bundle was built under, as Analysis's
 * `hmcanon/bundle_contract.py` declares it. Ids and hashes only. A bundle may leave it out; when
 * present it is whole, and null is refused.
 */
exports.bundleIntakeApprovalSchema = zod_1.z.strictObject({
    list_id: text(),
    ledger_run_id: text(),
    approval_sha256: zod_1.z.string().regex(SHA256),
    review_sha256: zod_1.z.string().regex(SHA256),
});
exports.bundleManifestLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("manifest"),
    format: zod_1.z.literal("VBLTC_BUNDLE_V1"),
    year: zod_1.z.int().min(2017).max(2100),
    is_synthetic: zod_1.z.boolean(),
    bundle_run_id: zod_1.z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/),
    analysis_commit: zod_1.z.string().regex(/^[0-9a-f]{7,40}$/),
    rule_set_version: text(),
    measures_run_id: text(),
    as_of_date: date(),
    created_at: zod_1.z.string().regex(DATE_TIME),
    gates: zod_1.z.strictObject(Object.fromEntries(exports.BUNDLE_GATES.map((g) => [g, gate()]))),
    counts: zod_1.z.strictObject({
        ...Object.fromEntries(exports.BUNDLE_ROW_KINDS.filter((k) => k !== "clinician").map((k) => [k, count()])),
        // J-273: a bundle written before the clinician line has no `clinician` count; absent means 0.
        clinician: count().optional(),
    }),
    hashes: zod_1.z.record(zod_1.z.string().regex(/^[^|]+[|](001|112|113|134|236)$/), manifestHashSchema),
    persons_not_matched: count(),
    intake_approval: exports.bundleIntakeApprovalSchema.optional(),
});
exports.bundleOrgLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("org"),
    aco_code: text(),
    tin: zod_1.z.string().regex(TIN),
    practice_name: text(),
    performance_group_name: text(),
});
exports.bundleFileLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("file"),
    file_id: text(),
    file_name: text(),
    sha256: zod_1.z.string().regex(SHA256),
    size_bytes: count(),
    layout: nullableText(),
    aco_code: nullableText(),
    disposition: zod_1.z.enum([
        "data",
        "reference",
        "system",
        "duplicate",
        "container",
        "blocked",
        "out_of_scope",
        "not_usable",
    ]),
    disposition_reason: nullableText(),
    records_read: count(),
    records_loaded: count(),
    records_set_aside: count(),
    set_aside_reasons: zod_1.z.record(text(), count()),
});
exports.bundlePersonLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("person"),
    person_key: text(),
    person_id: text(),
    aco_code: text(),
    first_name: nullableText(),
    last_name: nullableText(),
    birth_date: date().nullable(),
    sex: nullableText(),
    death_date: date().nullable(),
    medicare_qualifying: zod_1.z.boolean().nullable(),
    /**
     * J-274 (NR-05): the date the patient was discharged, when a source supplies one. Optional, unlike the
     * contract's other properties: absent or null means none, so bundles written before it load unchanged.
     */
    discharge_date: date().nullable().optional(),
});
exports.bundleRecordLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("record"),
    record_id: text(),
    table_name: text(),
    file_id: text(),
    source_locator: text(),
    person_key: text(),
    record_date: date().nullable(),
    code: nullableText(),
    code_system: nullableText(),
    modifiers: zod_1.z.array(text()),
    place_of_service: nullableText(),
    npi: zod_1.z.string().regex(NPI).nullable(),
    tin: zod_1.z.string().regex(TIN).nullable(),
    practice_name: nullableText(),
    measure_id: measureId().nullable(),
    meaning: nullableText(),
    billed: zod_1.z.boolean().nullable(),
    facility_name: facilityName(),
});
exports.bundlePmLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("pm"),
    pmr_id: text(),
    performance_year: count(),
    aco_code: text(),
    hmx_aco_org_id: nullableText(),
    hmx_aco_tin: nullableText(),
    measure_id: measureId(),
    person_key: text(),
    person_id: text(),
    hmx_matched: zod_1.z.boolean().nullable(),
    outcome: zod_1.z.enum(exports.BUNDLE_OUTCOMES),
    exclusion_element_id: nullableText(),
    exception_element_id: nullableText(),
    cms_qdc: nullableText(),
    outcome_reason: text(),
    evidence_basis: text(),
    gap_open: zod_1.z.boolean(),
    gap_status: text(),
    gap_action: nullableText(),
    deciding_activity_id: nullableText(),
    deciding_date: date().nullable(),
    deciding_visit_record_id: nullableText(),
    deciding_practice_tin: zod_1.z.string().regex(TIN).nullable(),
    deciding_performance_group: nullableText(),
    qualifying_visits: count(),
    first_qualifying_date: date(),
    last_qualifying_date: date(),
    age_anchor_date: date(),
    age_at_anchor: count(),
    medicare_qualifying: zod_1.z.boolean(),
    registry_reported: zod_1.z.boolean(),
    other_aco_lead: zod_1.z.boolean(),
    other_aco_lead_kind: nullableText(),
    rule_set_version: text(),
    owner_tin: zod_1.z.string().regex(TIN),
    owner_npi: zod_1.z.string().regex(NPI).nullable(),
    practice_name: text(),
    performance_group_name: text(),
    anchor_visit_date: date(),
});
exports.bundleGrpLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("grp"),
    pmr_id: text(),
    performance_group_name: text(),
});
exports.bundleActLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("act"),
    activity_id: text(),
    pmr_id: text(),
    person_key: text(),
    person_id: text(),
    aco_code: text(),
    measure_id: measureId(),
    visit_record_id: text(),
    encounter_ref: nullableText(),
    visit_date: date(),
    visit_aco_code: nullableText(),
    practice_tin: zod_1.z.string().regex(TIN).nullable(),
    practice_name: nullableText(),
    performance_group_name: nullableText(),
    hmx_performance_group_id: nullableText(),
    rendering_npi: zod_1.z.string().regex(NPI).nullable(),
    place_of_service: nullableText(),
    telehealth: zod_1.z.boolean().nullable(),
    medicare_qualifying: zod_1.z.boolean().nullable(),
    role: text(),
    effect: text(),
    evidence_code: nullableText(),
    evidence_code_system: nullableText(),
    evidence_meaning: nullableText(),
    element_id: nullableText(),
    rule_id: nullableText(),
    decided: zod_1.z.boolean(),
    superseded_by: nullableText(),
    not_counted_reason: nullableText(),
    record_ids: zod_1.z.array(text()).min(1),
    facility_name: facilityName(),
});
exports.bundleLabelLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("label"),
    label_kind: zod_1.z.enum(["vocab", "rule"]),
    field: text(),
    value: text(),
    label: text(),
});
/**
 * J-273: one clinician's name for the year, by NPI: `"First Last, Credential"` as Analysis formats it,
 * from the NPPES Registry or, when NPPES has no answer, the registry's registration list, as of `as_of`.
 * Analysis looks up no NPI that fails the check digit, so a line with one does not match the contract.
 */
exports.bundleClinicianLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("clinician"),
    npi: zod_1.z.string().regex(NPI).refine(npi_js_1.isValidNpi, "the NPI's check digit is wrong"),
    clinician_name: text(),
    name_source: zod_1.z.enum(exports.BUNDLE_CLINICIAN_NAME_SOURCES),
    as_of: date(),
});
exports.bundleTrailerLineSchema = zod_1.z.strictObject({
    kind: zod_1.z.literal("trailer"),
    lines: count(),
    sha256: zod_1.z.string().regex(SHA256),
});
/** Every line kind's schema, by kind. */
exports.BUNDLE_LINE_SCHEMAS = {
    manifest: exports.bundleManifestLineSchema,
    org: exports.bundleOrgLineSchema,
    file: exports.bundleFileLineSchema,
    person: exports.bundlePersonLineSchema,
    record: exports.bundleRecordLineSchema,
    pm: exports.bundlePmLineSchema,
    grp: exports.bundleGrpLineSchema,
    act: exports.bundleActLineSchema,
    label: exports.bundleLabelLineSchema,
    clinician: exports.bundleClinicianLineSchema,
    trailer: exports.bundleTrailerLineSchema,
};
exports.bundleLineSchema = zod_1.z.discriminatedUnion("kind", [
    exports.bundleManifestLineSchema,
    exports.bundleOrgLineSchema,
    exports.bundleFileLineSchema,
    exports.bundlePersonLineSchema,
    exports.bundleRecordLineSchema,
    exports.bundlePmLineSchema,
    exports.bundleGrpLineSchema,
    exports.bundleActLineSchema,
    exports.bundleLabelLineSchema,
    exports.bundleClinicianLineSchema,
    exports.bundleTrailerLineSchema,
]);
//# sourceMappingURL=bundle.js.map